/* eslint-disable no-bitwise */
import styled from '@emotion/styled';
import {Container, media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {SequenceItem} from 'UI/components/layout/SequenceBlock/SequenceItem';

import {useSequenceBlock} from './useSequenceBlock';

import type {ModuleIntro, SequenceBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `SequenceBlock` component.
 * This interface specifies the essential configuration needed to render an animated sequence timeline with progressive visualization.
 * Each property provides specific functionality for content display, block identification, introductory content, and testing instrumentation.
 */
interface ComponentProps {
    /**
     * Array of sequence items containing event data, descriptions, images, and titles for timeline display.
     * Each item represents a chronological event or milestone that will be rendered as part of the animated sequence.
     * The contents are used to generate the visual timeline with alternating positioning and scroll-triggered animations.
     */
    contents: SequenceBlockType['contents'];
    /**
     * Unique identifier for the sequence block used for block identification and linking purposes.
     * This identifier enables specific block targeting for navigation, analytics tracking, and content management operations.
     * The ID is also used as a base for generating child component test identifiers and accessibility attributes.
     */
    id: SequenceBlockType['id'];
    /**
     * Introduction content object containing heading, description, and other introductory elements for the block.
     * This content provides contextual information and sets up the sequence timeline with appropriate titles and descriptions.
     * The intro section appears above the timeline sequence and helps users understand the purpose and context of the displayed events.
     */
    intro: ModuleIntro;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing and automation processes.
     *
     * @default 'SequenceBlock'
     */
    testId?: string;
}

/**
 * The `SequenceBlock` component renders an animated timeline sequence with progressive visualization and scroll-based animation.
 * This component displays a chronological series of events or milestones in a visually engaging timeline format with animated progress indicators.
 * It features responsive grid layouts, alternating item positioning, and scroll-triggered animations to enhance user engagement and readability.
 *
 * @param props          The component props.
 * @param props.contents Array of sequence items containing event data, descriptions, images, and titles for timeline display.
 * @param props.id       Unique identifier for the sequence block used for block identification and linking purposes.
 * @param props.intro    Introduction content object containing heading, description, and other introductory elements for the block.
 * @param props.testId   Test identifier for component testing and automation, enabling reliable element selection during testing.
 * @returns The rendered sequence block component with animated timeline and progressive content reveal.
 *
 * @example
 * ```tsx
 * <SequenceBlock
 *     contents={[
 *         { id: '1', title: 'Event 1', description: 'First milestone', image: imageData },
 *         { id: '2', title: 'Event 2', description: 'Second milestone', image: imageData }
 *     ]}
 *     id="timeline-2024"
 *     intro={{ heading: 'Our Journey', description: 'Key milestones in our development' }}
 *     testId="company-timeline"
 * />
 * ```
 */
const SequenceBlock = observer(({contents, id, intro, testId = 'SequenceBlock'}: ComponentProps) => {
    const {firstItemRef, lastItemRef, lineEnd, lineRef, lineStart, scrollYProgress} = useSequenceBlock(contents);

    return (
        <Container>
            <BlockIntro blockId={id} intro={intro} testId={`${testId}-Intro`} />
            <Sequence>
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {contents?.map((event, index) => {
                    const {description, id: key, image, title: eventTitle} = event;
                    let ref;

                    if (index === 0) {
                        ref = firstItemRef;
                    } else if (index === contents.length - 1) {
                        ref = lastItemRef;
                    }

                    return (
                        <SequenceItem
                            key={key}
                            ref={ref}
                            description={description}
                            image={image}
                            isEven={!(index & 1)}
                            itemTitle={eventTitle}
                            testId={`${testId}-item-${id}`}
                        />
                    );
                })}
                <SequenceLineWrapper aria-hidden="true">
                    <SequenceLine
                        ref={lineRef}
                        style={{
                            bottom: `${lineEnd}px`,
                            top: `${lineStart}px`
                        }}
                    >
                        <SequenceLineProgress style={{scaleY: scrollYProgress}} />
                    </SequenceLine>
                </SequenceLineWrapper>
            </Sequence>
        </Container>
    );
});

SequenceBlock.displayName = 'SequenceBlock';

export {SequenceBlock};

const Sequence = styled.ol`
    appearance: none;
    display: grid;
    gap: ${spacing(4)};
    grid-auto-rows: auto;
    grid-template-columns: 8rem 1fr;
    isolation: isolate;
    margin: 0;
    margin-block-start: ${spacing(-4)};
    max-width: 100%;
    padding: 0;
    position: relative;
    width: 100%;

    ${media('md')} {
        grid-template-columns: 1fr 12rem 1fr;
    }
`;

const SequenceLineWrapper = styled.li`
    align-items: center;
    display: grid;
    grid-column: 1 / 1;
    grid-row: 1 / -1;
    grid-template-columns: subgrid;
    grid-template-rows: subgrid;
    justify-content: center;
    max-width: 100%;
    min-width: 0px;
    width: 100%;

    ${media('md')} {
        grid-column: 2 / 2;
    }
`;


const SequenceLine = styled.div`
    background-color: ${({theme}) => theme.colors.neutral};
    display: flex;
    justify-self: center;
    position: absolute;
    width: 5px;
    z-index: -1;
`;

const SequenceLineProgress = styled(motion.div)`
    background-color: ${({theme}) => theme.colors.primary};
    height: 100%;
    position: absolute;
    scaleY: 0;
    top: 0;
    transform-origin: top center;
    width: 5px;
`;